import React, { useMemo, useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";

const SetClaimState = () => {
  const [rawClaimCodes, setRawClaimCodes] = useState("");
  const [selectedClaimStateId, setSelectedClaimStateId] = useState("");
  const [claimStates, setClaimStates] = useState([]);
  const [isLoadingClaimStates, setIsLoadingClaimStates] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitResult, setSubmitResult] = useState(null);

  const parsedClaimCodes = useMemo(() => {
    const splitCodes = rawClaimCodes.split(/[\n,;\s]+/g);
    const cleanedCodes = splitCodes.map((code) => code.trim()).filter((code) => code.length > 0);
    return [...new Set(cleanedCodes)];
  }, [rawClaimCodes]);

  useEffect(() => {
    let isCancelled = false;

    const loadClaimStates = async () => {
      try {
        const response = await axios.get("/api/v1/claimStates");
        if (!isCancelled) {
          setClaimStates(response.data || []);
        }
      } catch (error) {
        if (!isCancelled) {
          setSubmitError("Could not load claim states.");
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingClaimStates(false);
        }
      }
    };

    loadClaimStates();

    return () => {
      isCancelled = true;
    };
  }, []);

  const handleSubmit = async () => {
    setSubmitError("");
    setSubmitResult(null);

    if (!selectedClaimStateId) {
      setSubmitError("Please select a target claim state.");
      return;
    }

    if (parsedClaimCodes.length === 0) {
      setSubmitError("Please enter at least one claim code.");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post("/api/v1/claims/bulk-set-claim-state", {
        claimCodes: parsedClaimCodes,
        claimStateId: Number(selectedClaimStateId),
      });

      setSubmitResult(response.data);
    } catch (error) {
      const apiError = error?.response?.data;
      setSubmitError(typeof apiError === "string" ? apiError : "Bulk update failed.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Grid container spacing={6}>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Set Claim State
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Enter one or more claim codes and update them to a single claim state.
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={6}
                  label="Claim Codes"
                  value={rawClaimCodes}
                  onChange={(event) => setRawClaimCodes(event.target.value)}
                  placeholder="Example: NL123456, NL123457 or one code per line"
                />
                <Typography variant="caption" color="text.secondary">
                  Parsed claim codes: {parsedClaimCodes.length}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={isLoadingClaimStates || isSubmitting}>
                  <InputLabel id="target-claim-state-label">Target Claim State</InputLabel>
                  <Select
                    labelId="target-claim-state-label"
                    value={selectedClaimStateId}
                    label="Target Claim State"
                    onChange={(event) => setSelectedClaimStateId(event.target.value)}
                  >
                    {claimStates.map((claimState) => (
                      <MenuItem key={claimState.claimStateId} value={String(claimState.claimStateId)}>
                        {claimState.shortCode} - {claimState.description}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={isSubmitting || isLoadingClaimStates}
                >
                  {isSubmitting ? <CircularProgress size={20} color="inherit" /> : "Update Claim State"}
                </Button>
              </Grid>
            </Grid>

            {submitError && (
              <Box mt={3}>
                <Alert severity="error">{submitError}</Alert>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      {submitResult && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Bulk Update Result
              </Typography>

              <Typography variant="body2">
                Requested: {submitResult.requestedCount} | Updated: {submitResult.updatedCount} | Not Found:{" "}
                {submitResult.notFoundCount} | Failed: {submitResult.failedCount}
              </Typography>

              <Grid container spacing={2} mt={0.5}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2">Updated Codes</Typography>
                  <List dense>
                    {(submitResult.updatedCodes || []).map((code) => (
                      <ListItem key={`updated-${code}`} disableGutters>
                        <ListItemText primary={code} />
                      </ListItem>
                    ))}
                  </List>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2">Not Found Codes</Typography>
                  <List dense>
                    {(submitResult.notFoundCodes || []).map((code) => (
                      <ListItem key={`notfound-${code}`} disableGutters>
                        <ListItemText primary={code} />
                      </ListItem>
                    ))}
                  </List>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2">Failed Codes</Typography>
                  <List dense>
                    {(submitResult.failedItems || []).map((item) => (
                      <ListItem key={`failed-${item.claimCode}`} disableGutters>
                        <ListItemText primary={item.claimCode} secondary={item.reason} />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
};

export default SetClaimState;
